/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Server, Shield, ShieldCheck, ShieldAlert, KeyRound, Globe, Mail,
  CreditCard, Plane, ChevronRight, X, RefreshCw, Clock, CheckCircle,
  AlertTriangle, Power, History, FileText, Edit3, Zap, ArrowLeftRight,
  Loader2, Database, Search,
} from 'lucide-react';
import { toast } from 'sonner';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const TRIPJACK_DEFAULT_BASE_URL = {
  uat: 'https://apitest.tripjack.com',
  production: 'https://tripjack.com',
};

function buildEditInitialValues(row, env, meta) {
  const block = row[env] || {};
  const out = {};
  for (const f of meta?.fields || []) {
    if (f.secret) continue;
    const v = block[f.key];
    if (v && v !== '(unset)' && !String(v).includes('***')) {
      out[f.key] = v;
    }
  }
  if (row.supplier === 'tripjack' && !out.base_url) {
    out.base_url = TRIPJACK_DEFAULT_BASE_URL[env] || TRIPJACK_DEFAULT_BASE_URL.uat;
  }
  return out;
}

const SUPPLIER_META = {
  tripjack: {
    label: 'Tripjack — Flight & Hotel API',
    icon: Plane,
    accent: 'from-sky-600 to-sky-700',
    tone: 'bg-sky-50 border-sky-200 text-sky-900',
    fields: [
      { key: 'api_key', label: 'API Key', secret: true },
      { key: 'base_url', label: 'Base URL', secret: false },
    ],
    docs: 'apikey → server-only; URL: tripjack.com (prod) / apitest.tripjack.com (UAT)',
  },
  riya: {
    label: 'Riya Travel — Flight API',
    icon: Plane,
    accent: 'from-indigo-600 to-indigo-700',
    tone: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    fields: [
      { key: 'username', label: 'Username', secret: true },
      { key: 'password', label: 'Password', secret: true },
      { key: 'api_key', label: 'API Key / Token (optional)', secret: true },
      { key: 'agent_id', label: 'Agent ID', secret: true },
      { key: 'terminal_id', label: 'Terminal ID', secret: true },
      { key: 'base_url', label: 'Base URL', secret: false },
    ],
    docs: 'Live auth uses username+password. API key is optional. Agent/terminal IDs required by RWS wire format.',
  },
  razorpay: {
    label: 'Razorpay — Payment Gateway',
    icon: CreditCard,
    accent: 'from-emerald-600 to-emerald-700',
    tone: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    fields: [
      { key: 'key_id', label: 'Key ID', secret: true },
      { key: 'key_secret', label: 'Key Secret', secret: true },
      { key: 'webhook_secret', label: 'Webhook Secret', secret: true },
      { key: 'environment', label: 'Environment (test / production)', secret: false },
    ],
    docs: 'Test keys from Razorpay Dashboard → API Keys. Webhook URL: {BACKEND}/api/payment/razorpay/webhook',
  },
  smtp: {
    label: 'SMTP — Email Delivery',
    icon: Mail,
    accent: 'from-amber-600 to-amber-700',
    tone: 'bg-amber-50 border-amber-200 text-amber-900',
    fields: [
      { key: 'host', label: 'Host', secret: false },
      { key: 'port', label: 'Port', secret: false },
      { key: 'username', label: 'Username', secret: true },
      { key: 'password', label: 'Password / App-Token', secret: true },
      { key: 'from_email', label: 'From Email', secret: false },
      { key: 'from_name', label: 'From Name', secret: false },
    ],
    docs: 'No UAT/Production tiers — single credential set + global enable toggle.',
  },
};


function fmtIST(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
}


function EnvironmentPill({ value, isActive }) {
  const isProd = value === 'production';
  const base = isActive
    ? (isProd ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white')
    : (isProd ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200');
  return (
    <span
      data-testid={`supplier-env-pill-${value}${isActive ? '-active' : ''}`}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${base}`}
    >
      {isProd ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
      {isProd ? 'Production' : 'UAT'}
      {isActive && <span className="ml-1 opacity-90">· active</span>}
    </span>
  );
}


function EnabledBadge({ enabled }) {
  return (
    <span
      data-testid={`supplier-enabled-${enabled ? 'yes' : 'no'}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
        enabled
          ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
          : 'bg-rose-100 text-rose-800 border border-rose-300'
      }`}
    >
      <Power className="w-3 h-3" />
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}


function SourceBadge({ source }) {
  const isDb = source === 'db';
  return (
    <span
      data-testid={`supplier-source-${source}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
        isDb
          ? 'bg-violet-50 text-violet-700 border border-violet-200'
          : 'bg-slate-50 text-slate-600 border border-slate-200'
      }`}
      title={isDb ? 'Loaded from MongoDB override' : 'Falling back to .env'}
    >
      {isDb ? 'DB' : '.env'}
    </span>
  );
}


// ─── Phase 6 modals ──────────────────────────────────────────────────

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-bold text-[#1B375B]">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}


function TestResultBox({ result }) {
  if (!result) return null;
  const cls = result.ok
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  const Icon = result.ok ? CheckCircle : AlertTriangle;
  return (
    <div
      className={`border rounded-lg p-3 text-xs ${cls}`}
      data-testid="supplier-test-result"
    >
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">
            {result.ok ? 'Connection OK' : 'Connection failed'}
            {result.latency_ms !== undefined && (
              <span className="text-[10px] opacity-70 ml-2">{result.latency_ms} ms</span>
            )}
          </p>
          <p className="leading-snug break-words">{result.message}</p>
        </div>
      </div>
    </div>
  );
}


function EditCredentialsModal({ row, onClose, onSaved }) {
  const meta = SUPPLIER_META[row.supplier];
  const initialEnv = row.active_environment || 'uat';
  const [env, setEnv] = useState(initialEnv);
  const [values, setValues] = useState(() => buildEditInitialValues(row, initialEnv, meta));
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setValues(buildEditInitialValues(row, env, meta));
    setTested(false);
    setTestResult(null);
  }, [env, row, meta]);

  const setField = (k, v) => {
    setValues((p) => ({ ...p, [k]: v }));
    setTested(false);
    setTestResult(null);
  };

  const runTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/${row.supplier}/test-connection`,
        { environment: env, credentials: values },
        { withCredentials: true },
      );
      setTestResult(r.data);
      setTested(!!r.data?.ok);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || e.message });
      setTested(false);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!tested) {
      toast.error('Run a successful connection test before saving.');
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/supplier-configs/${row.supplier}`,
        { environment: env, credentials: values },
        { withCredentials: true },
      );
      toast.success(`${row.supplier} ${env} credentials saved.`);
      onSaved();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === 'object' ? d.message : d) || e.message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Edit ${meta?.label || row.supplier}`} onClose={onClose} testid="supplier-edit-modal">
      <div className="mb-4">
        <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1">
          Environment tier
        </p>
        <div className="flex gap-2">
          {['uat', 'production'].map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEnv(e)}
              data-testid={`supplier-edit-env-${e}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${
                env === e
                  ? (e === 'production' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-amber-500 text-white border-amber-500')
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#1B375B]'
              }`}
            >
              {e.toUpperCase()}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-400 mt-1">
          Save credentials for one tier at a time. Active = <strong>{row.active_environment}</strong>.
        </p>
      </div>

      <div className="space-y-3 mb-4">
        {meta?.fields?.map((f) => (
          <div key={f.key}>
            <label className="block text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1">
              {f.label}
              {f.secret && <span className="text-rose-500 ml-1">●</span>}
            </label>
            <input
              type={f.secret ? 'password' : 'text'}
              value={values[f.key] || ''}
              onChange={(e) => setField(f.key, e.target.value)}
              autoComplete="new-password"
              data-testid={`supplier-edit-field-${f.key}`}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1B375B] font-mono"
              placeholder={f.secret ? 'Enter new value to replace' : f.label}
            />
          </div>
        ))}
      </div>

      <div className="mb-3">
        <TestResultBox result={testResult} />
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={runTest}
          disabled={busy}
          data-testid="supplier-edit-test-btn"
          className="px-3 py-2 text-xs font-semibold rounded-lg bg-white border border-[#1B375B] text-[#1B375B] inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          Test connection
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy || !tested}
          data-testid="supplier-edit-save-btn"
          className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#1B375B] text-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
          {tested ? `Save ${env}` : 'Test first to enable Save'}
        </button>
      </div>
    </ModalShell>
  );
}


function ToggleModal({ row, onClose, onDone }) {
  const targetEnabled = !row.enabled;
  const expected = `${row.supplier} ${targetEnabled ? 'ENABLE' : 'DISABLE'}`;
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const phraseOk = phrase.trim() === expected;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/${row.supplier}/toggle`,
        { enabled: targetEnabled, confirm_phrase: phrase.trim() },
        { withCredentials: true },
      );
      toast.success(r.data?.message);
      onDone();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === 'object' ? d.message : d) || e.message;
      setErr({ message: msg, inflight: d?.inflight_count });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`${targetEnabled ? 'Enable' : 'Disable'} ${row.supplier}`}
      onClose={onClose}
      testid="supplier-toggle-modal"
    >
      <div className={`rounded-lg p-3 mb-4 border ${
        targetEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
      }`}>
        <p className="text-xs font-semibold mb-1">
          {targetEnabled
            ? `${row.supplier} will accept new bookings again.`
            : `${row.supplier} will stop accepting new bookings.`}
        </p>
        <p className="text-[11px] text-gray-700 leading-snug">
          {targetEnabled
            ? 'Search → review → book paths resume immediately. In-flight bookings unaffected.'
            : 'In-flight bookings (PNR generated / awaiting ticket / payment pending supplier) continue to settle. Only NEW bookings are blocked. A live-traffic guard refuses the disable if any booking is mid-flight.'}
        </p>
      </div>

      <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1">
        Confirm by typing
      </p>
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-2 font-mono text-xs text-[#1B375B]">
        {expected}
      </div>
      <input
        type="text"
        value={phrase}
        onChange={(e) => { setPhrase(e.target.value); setErr(null); }}
        data-testid="supplier-toggle-phrase"
        autoComplete="off"
        className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:border-[#1B375B] mb-3"
        placeholder="Type the phrase above to confirm"
      />

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-900 text-xs p-3 rounded-lg mb-3" data-testid="supplier-toggle-error">
          <p className="font-semibold">{err.message}</p>
          {err.inflight !== undefined && (
            <p className="mt-1">
              {err.inflight} mid-flight booking(s). Resolve them via{' '}
              <a className="underline" href="/admin/ops-monitor">Ops Monitor</a> first.
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!phraseOk || busy}
        data-testid="supplier-toggle-submit"
        className={`w-full py-2.5 text-sm font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed ${
          targetEnabled ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
        } inline-flex items-center justify-center gap-2`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
        {targetEnabled ? `Enable ${row.supplier}` : `Disable ${row.supplier}`}
      </button>
    </ModalShell>
  );
}


function SwitchEnvModal({ row, onClose, onDone }) {
  const target = row.active_environment === 'uat' ? 'production' : 'uat';
  const expected = `${row.supplier} ${target.toUpperCase()}`;
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const phraseOk = phrase.trim() === expected;
  const hasTarget = target === 'uat' ? row.has_uat : row.has_production;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/${row.supplier}/switch-environment`,
        { target_environment: target, confirm_phrase: phrase.trim() },
        { withCredentials: true },
      );
      toast.success(`${row.supplier} switched to ${r.data?.new_environment}`);
      onDone();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === 'object' ? d.message : d) || e.message;
      setErr({ message: msg, test: d?.test_result });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`Switch ${row.supplier} → ${target.toUpperCase()}`}
      onClose={onClose}
      testid="supplier-switchenv-modal"
    >
      {!hasTarget && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded-lg mb-3" data-testid="supplier-switchenv-missing">
          <p className="font-semibold">No {target.toUpperCase()} credentials saved.</p>
          <p className="mt-1">Save credentials for {target} first via the Edit modal.</p>
        </div>
      )}
      <div className={`rounded-lg p-3 mb-4 border ${
        target === 'production' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <p className="text-xs font-semibold mb-1">
          {row.active_environment.toUpperCase()} → <strong>{target.toUpperCase()}</strong>
        </p>
        <p className="text-[11px] text-gray-700 leading-snug">
          A connection test against the <strong>{target}</strong> credentials runs first.
          The switch is rejected if the test fails.
          {target === 'production' && (
            <span className="block mt-1 font-semibold text-emerald-900">
              This will route real customer traffic to production.
            </span>
          )}
        </p>
      </div>

      <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1">
        Confirm by typing
      </p>
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-2 font-mono text-xs text-[#1B375B]">
        {expected}
      </div>
      <input
        type="text"
        value={phrase}
        onChange={(e) => { setPhrase(e.target.value); setErr(null); }}
        data-testid="supplier-switchenv-phrase"
        autoComplete="off"
        className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:border-[#1B375B] mb-3"
        placeholder="Type the phrase above to confirm"
      />

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-900 text-xs p-3 rounded-lg mb-3" data-testid="supplier-switchenv-error">
          <p className="font-semibold">{err.message}</p>
          {err.test && <p className="mt-1 opacity-80">Test result: {err.test.message}</p>}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!phraseOk || !hasTarget || busy}
        data-testid="supplier-switchenv-submit"
        className={`w-full py-2.5 text-sm font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed text-white inline-flex items-center justify-center gap-2 ${
          target === 'production' ? 'bg-emerald-600' : 'bg-amber-600'
        }`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
        Switch to {target.toUpperCase()}
      </button>
    </ModalShell>
  );
}


function CredentialRow({ label, value, secret }) {
  const isUnset = !value || value === '(unset)';
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        {secret ? <KeyRound className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                : <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
        <span className="text-xs font-semibold text-gray-700">{label}</span>
      </div>
      <span
        className={`font-mono text-xs truncate ml-3 ${
          isUnset ? 'text-gray-400 italic' : (secret ? 'text-[#1B375B]' : 'text-gray-700')
        }`}
        title={value || ''}
      >
        {value || '(unset)'}
      </span>
    </div>
  );
}


function SupplierCard({ row, onView, onAudit, onEdit, onToggle, onSwitchEnv, onQuickTest, quickTesting }) {
  const meta = SUPPLIER_META[row.supplier];
  const Icon = meta?.icon || Server;
  const activeBlock = row[row.active_environment] || {};
  const otherEnv = row.active_environment === 'uat' ? 'production' : 'uat';
  const otherHas = row.active_environment === 'uat' ? row.has_production : row.has_uat;

  return (
    <div
      data-testid={`supplier-card-${row.supplier}`}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${meta?.accent || 'from-slate-600 to-slate-700'} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2.5 text-white min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <h3 className="font-bold text-sm truncate" data-testid={`supplier-label-${row.supplier}`}>
            {meta?.label || row.supplier}
          </h3>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <SourceBadge source={row.source} />
          <EnabledBadge enabled={row.enabled} />
        </div>
      </div>

      {/* Env strip */}
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-2 border-b border-gray-100">
        <EnvironmentPill value={row.active_environment} isActive />
        {otherHas && (
          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
            (also configured:
            <EnvironmentPill value={otherEnv} isActive={false} />
            )
          </span>
        )}
      </div>

      {/* Active credentials */}
      <div className="px-4 py-3">
        <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-2">
          Active credentials ({row.active_environment})
        </p>
        {meta?.fields?.length ? (
          <div className="space-y-0.5">
            {meta.fields.map((f) => (
              <CredentialRow
                key={f.key}
                label={f.label}
                value={activeBlock?.[f.key]}
                secret={f.secret}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No field schema configured.</p>
        )}
      </div>

      {/* Meta footer */}
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
        <span className="inline-flex items-center gap-1" title="Last test outcome">
          {row.last_tested_outcome === 'success' ? (
            <CheckCircle className="w-3 h-3 text-emerald-600" />
          ) : row.last_tested_outcome === 'failed' ? (
            <AlertTriangle className="w-3 h-3 text-rose-600" />
          ) : (
            <Clock className="w-3 h-3 text-gray-400" />
          )}
          {row.last_tested_at ? `Tested ${fmtIST(row.last_tested_at)}` : 'Never tested'}
        </span>
        {row.last_updated_at && (
          <span className="inline-flex items-center gap-1" title="Last updated">
            <RefreshCw className="w-3 h-3 text-gray-400" />
            {fmtIST(row.last_updated_at)}
            {row.last_updated_by_email ? ` · ${row.last_updated_by_email}` : ''}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => onQuickTest(row)}
            disabled={quickTesting}
            data-testid={`supplier-quick-test-${row.supplier}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-50"
          >
            {quickTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Test
          </button>
          <button
            type="button"
            onClick={() => onSwitchEnv(row)}
            data-testid={`supplier-switchenv-btn-${row.supplier}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:underline"
            title={`Switch to ${otherEnv.toUpperCase()}`}
          >
            <ArrowLeftRight className="w-3 h-3" /> Switch env
          </button>
          <button
            type="button"
            onClick={() => onToggle(row)}
            data-testid={`supplier-toggle-btn-${row.supplier}`}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold hover:underline ${row.enabled ? 'text-rose-700' : 'text-emerald-700'}`}
          >
            <Power className="w-3 h-3" /> {row.enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            onClick={() => onEdit(row)}
            data-testid={`supplier-edit-btn-${row.supplier}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B375B] hover:underline"
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
          <button
            type="button"
            onClick={() => onAudit(row)}
            data-testid={`supplier-audit-btn-${row.supplier}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B375B] hover:underline"
          >
            <History className="w-3 h-3" /> Audit
          </button>
          <button
            type="button"
            onClick={() => onView(row)}
            data-testid={`supplier-view-btn-${row.supplier}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B375B] hover:underline"
          >
            Details <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      <p className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100">
        {meta?.docs}
      </p>
    </div>
  );
}


function DetailsDrawer({ row, onClose }) {
  if (!row) return null;
  const meta = SUPPLIER_META[row.supplier];
  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="supplier-details-drawer"
    >
      <div
        className="bg-white w-full max-w-md h-full overflow-y-auto p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1B375B]">{meta?.label}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="supplier-details-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {['uat', 'production'].map((env) => {
          const has = env === 'uat' ? row.has_uat : row.has_production;
          const block = row[env] || {};
          const isActive = row.active_environment === env;
          return (
            <section key={env} className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <EnvironmentPill value={env} isActive={isActive} />
                {!has && <span className="text-[11px] text-gray-400 italic">not configured</span>}
              </div>
              {has ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-0.5">
                  {meta?.fields?.map((f) => (
                    <CredentialRow key={f.key} label={f.label} value={block?.[f.key]} secret={f.secret} />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 px-3">No credentials stored for {env}.</p>
              )}
            </section>
          );
        })}

        <section className="mt-5 pt-4 border-t border-gray-200">
          <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-2">
            Edit · Test · Toggle
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
            <p className="font-semibold mb-1">Live write surface enabled</p>
            <p className="leading-relaxed">
              Close this drawer and use the per-card buttons:{' '}
              <strong>Edit</strong> (form + mandatory test before save),
              <strong> Test</strong> (live connection check),
              <strong> Switch env</strong> (type-to-confirm UAT↔Prod),
              <strong> Disable / Enable</strong> (type-to-confirm,
              live-traffic guard on disable). Every change is
              audit-logged.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}


function AuditDrawer({ supplier, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!supplier) return undefined;
    let active = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/supplier-configs/${supplier}/audit`,
          { withCredentials: true, params: { limit: 50 } },
        );
        if (active) setRows(r.data?.rows || []);
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || 'Failed to load audit log');
      }
    })();
    return () => { active = false; };
  }, [supplier]);

  if (!supplier) return null;
  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="supplier-audit-drawer"
    >
      <div
        className="bg-white w-full max-w-md h-full overflow-y-auto p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1B375B] inline-flex items-center gap-2">
            <History className="w-5 h-5" /> Audit log · {supplier}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="supplier-audit-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {err && <p className="text-rose-700 text-sm">{err}</p>}
        {rows && rows.length === 0 && (
          <p className="text-sm text-gray-500 italic">No changes recorded yet.</p>
        )}
        {rows && rows.length > 0 && (
          <ul className="space-y-2.5">
            {rows.map((r, i) => (
              <li key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-[#1B375B]">{r.action}</span>
                  <span className="text-gray-500">{fmtIST(r.ts)}</span>
                </div>
                {r.old_environment && r.new_environment && (
                  <p className="text-gray-700">
                    {r.old_environment} → <strong>{r.new_environment}</strong>
                  </p>
                )}
                {r.changed_fields?.length > 0 && (
                  <p className="text-gray-600">fields: {r.changed_fields.join(', ')}</p>
                )}
                <p className="text-gray-500 mt-1">
                  by {r.admin_email || r.admin_id || 'system'}
                  {r.outcome === 'failed' && <span className="ml-2 text-rose-600">· failed: {r.reason}</span>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


export default function SupplierSettings() {
  const [rows, setRows] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [auditing, setAuditing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [switchingEnv, setSwitchingEnv] = useState(null);
  const [quickTestingSupplier, setQuickTestingSupplier] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/supplier-configs`, { withCredentials: true }),
        axios.get(`${API}/preflight`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setRows(r1.data?.rows || []);
      setPreflight(r2.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load supplier settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const quickTest = async (row) => {
    setQuickTestingSupplier(row.supplier);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/${row.supplier}/test-connection`,
        {},
        { withCredentials: true },
      );
      if (r.data?.ok) toast.success(`${row.supplier}: ${r.data.message}`);
      else toast.error(`${row.supplier} test failed: ${r.data?.message || 'unknown'}`);
      fetchAll();
    } catch (e) {
      toast.error(`${row.supplier}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setQuickTestingSupplier(null);
    }
  };

  const runMigrate = async () => {
    if (!window.confirm('Bulk-migrate current .env values into the DB? This is idempotent — already-seeded suppliers are skipped.')) return;
    setMigrating(true);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/migrate-env-to-db`,
        {},
        { withCredentials: true },
      );
      toast.success(`Migration done. Seeded: ${r.data?.seeded?.length || 0} · Skipped: ${r.data?.skipped?.length || 0} · Failed: ${r.data?.failed?.length || 0}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setMigrating(false);
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const runRefreshNow = async (scope = 'all') => {
    if (!window.confirm(
      scope === 'all'
        ? 'Hot-reload credentials for ALL suppliers? This forces every running request to use the latest DB values WITHOUT a backend restart.'
        : `Hot-reload ${scope} credentials? Forces in-process caches to refresh from the DB.`,
    )) return;
    setRefreshing(true);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/${scope}/refresh-now`,
        {},
        { withCredentials: true },
      );
      const reset = (r.data?.singletons_reset || []).join(', ') || 'none';
      const refreshed = (r.data?.refreshed || []).join(', ');
      toast.success(`Refreshed: ${refreshed}. Singletons reset: ${reset}.`, { duration: 6000 });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const [debugBusy, setDebugBusy] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const runTripjackDebug = async () => {
    setDebugBusy(true);
    setDebugResult(null);
    try {
      const r = await axios.post(
        `${API}/supplier-configs/tripjack/debug-search`,
        {},
        { withCredentials: true },
      );
      setDebugResult(r.data);
      if (r.data?.success) {
        toast.success(`Live search returned ${r.data.result_count} flights — Tripjack is healthy.`, { duration: 6000 });
      } else {
        toast.error(`Tripjack diagnosis: ${r.data?.diagnosis || 'unknown'}. See modal for details.`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDebugBusy(false);
    }
  };

  const summary = useMemo(() => {
    const enabled = rows.filter((r) => r.enabled).length;
    const prod = rows.filter((r) => r.active_environment === 'production').length;
    const dbBacked = rows.filter((r) => r.source === 'db').length;
    return { enabled, total: rows.length, prod, dbBacked };
  }, [rows]);

  const allDbBacked = summary.dbBacked === summary.total && summary.total > 0;

  return (
    <div className="p-6" data-testid="supplier-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] inline-flex items-center gap-2">
            <Server className="w-6 h-6" />
            Supplier Settings
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage Tripjack · Riya · Razorpay · SMTP credentials — UAT &amp; Production tiers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runTripjackDebug}
            disabled={debugBusy}
            data-testid="supplier-tripjack-debug-btn"
            className="px-3 py-2 text-xs font-semibold rounded-lg bg-sky-600 text-white inline-flex items-center gap-1.5 hover:bg-sky-700 disabled:opacity-50"
            title="Issue a real BOM→DXB live search and surface Tripjack's exact rejection reason"
          >
            {debugBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Debug Tripjack live search
          </button>
          <button
            onClick={() => runRefreshNow('all')}
            disabled={refreshing}
            data-testid="supplier-refresh-now-btn"
            className="px-3 py-2 text-xs font-semibold rounded-lg bg-amber-500 text-white inline-flex items-center gap-1.5 hover:bg-amber-600 disabled:opacity-50"
            title="Hot-reload all supplier credentials without restarting the backend"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh credentials now
          </button>
          {!allDbBacked && (
            <button
              onClick={runMigrate}
              disabled={migrating}
              data-testid="supplier-migrate-btn"
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-violet-600 text-white inline-flex items-center gap-1.5 hover:bg-violet-700 disabled:opacity-50"
              title="One-time: read current .env values, encrypt, seed into DB"
            >
              {migrating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
              Migrate .env → DB
            </button>
          )}
          <button
            onClick={fetchAll}
            data-testid="supplier-settings-refresh"
            className="px-3 py-2 text-xs font-semibold rounded-lg bg-white border border-gray-200 hover:border-[#1B375B] inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="supplier-summary-enabled">
          <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">Enabled</p>
          <p className="text-2xl font-bold text-emerald-700 mt-0.5">
            {summary.enabled}<span className="text-sm text-gray-400 ml-1">/ {summary.total}</span>
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="supplier-summary-prod">
          <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">On production</p>
          <p className="text-2xl font-bold text-[#1B375B] mt-0.5">
            {summary.prod}<span className="text-sm text-gray-400 ml-1">/ {summary.total}</span>
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="supplier-summary-dbbacked">
          <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">DB-backed</p>
          <p className="text-2xl font-bold text-violet-700 mt-0.5">
            {summary.dbBacked}<span className="text-sm text-gray-400 ml-1">/ {summary.total}</span>
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {summary.dbBacked === 0 ? 'All from .env' : (allDbBacked ? 'All overrides active' : 'Partial override')}
          </p>
        </div>
        <div
          className={`bg-white border rounded-xl p-3 ${preflight?.ok ? 'border-emerald-200' : 'border-rose-200'}`}
          data-testid="supplier-summary-preflight"
        >
          <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">Preflight</p>
          <p className={`text-2xl font-bold mt-0.5 inline-flex items-center gap-1 ${
            preflight?.ok ? 'text-emerald-700' : 'text-rose-700'
          }`}>
            {preflight?.ok ? <ShieldCheck className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
            {preflight?.ok ? 'OK' : `${preflight?.critical_failure_count ?? '—'} crit`}
          </p>
          {!preflight?.ok && (preflight?.critical_failures?.length ?? 0) > 0 && (
            <p className="text-[10px] text-rose-600 mt-0.5 truncate" title={preflight.critical_failures.join(', ')}>
              {preflight.critical_failures.slice(0, 2).join(', ')}…
            </p>
          )}
        </div>
      </div>

      {/* Phase-5/6 active info banner */}
      <div className="mb-5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-3 flex items-start gap-2.5 text-xs"
           data-testid="supplier-readonly-banner">
        <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="leading-relaxed">
          <strong className="block">Write surface enabled (Phase 5 + 6)</strong>
          You can now <strong>Edit credentials</strong> (mandatory connection test before save),
          <strong> Toggle</strong> supplier on/off (live-traffic guard for disable), and
          <strong> Switch env</strong> UAT&nbsp;↔&nbsp;Production (type-to-confirm + pre-flight test).
          All changes are append-only audit-logged. Use{' '}
          <strong>Migrate .env → DB</strong> once to bootstrap if the dashboard still shows{' '}
          <code className="font-mono bg-emerald-100 px-1 rounded">.env</code> as the source.
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-900 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Supplier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && rows.length === 0 && (
          <div className="col-span-full text-center py-8 text-sm text-gray-500">
            Loading supplier configurations…
          </div>
        )}
        {!loading && rows.length === 0 && !error && (
          <div className="col-span-full text-center py-8 text-sm text-gray-500">
            No suppliers configured yet.
          </div>
        )}
        {rows.map((r) => (
          <SupplierCard
            key={r.supplier}
            row={r}
            onView={setViewing}
            onAudit={(row) => setAuditing(row.supplier)}
            onEdit={setEditing}
            onToggle={setToggling}
            onSwitchEnv={setSwitchingEnv}
            onQuickTest={quickTest}
            quickTesting={quickTestingSupplier === r.supplier}
          />
        ))}
      </div>

      <DetailsDrawer row={viewing} onClose={() => setViewing(null)} />
      <AuditDrawer supplier={auditing} onClose={() => setAuditing(null)} />
      {editing && (
        <EditCredentialsModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchAll}
        />
      )}
      {toggling && (
        <ToggleModal
          row={toggling}
          onClose={() => setToggling(null)}
          onDone={fetchAll}
        />
      )}
      {switchingEnv && (
        <SwitchEnvModal
          row={switchingEnv}
          onClose={() => setSwitchingEnv(null)}
          onDone={fetchAll}
        />
      )}

      {/* Tripjack live-search diagnostic modal — surfaces the EXACT supplier
          rejection (Access Denied / wallet zero / PCC mismatch / etc.) */}
      {debugResult && (
        <div
          data-testid="tripjack-debug-modal"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDebugResult(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  debugResult.success ? 'bg-emerald-100' : 'bg-rose-100'
                }`}>
                  {debugResult.success
                    ? <CheckCircle className="w-5 h-5 text-emerald-600" />
                    : <AlertTriangle className="w-5 h-5 text-rose-600" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Tripjack live-search probe</h3>
                  <p className="text-xs text-gray-500">
                    Sector {debugResult.sector} · {debugResult.travel_date}
                  </p>
                </div>
              </div>
              <button onClick={() => setDebugResult(null)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">Env:</span> <strong>{debugResult.tripjack_env}</strong></div>
                <div><span className="text-gray-500">Result count:</span> <strong>{debugResult.result_count}</strong></div>
                <div className="col-span-2"><span className="text-gray-500">Base URL:</span> <code className="text-[11px]">{debugResult.tripjack_base_url}</code></div>
                <div className="col-span-2"><span className="text-gray-500">API key:</span> <code className="text-[11px]">{debugResult.api_key_masked}</code></div>
              </div>
              <div className={`p-3 rounded-lg border ${
                debugResult.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}>
                <p className="text-xs font-bold uppercase tracking-wide mb-1">Diagnosis</p>
                <p className="text-base font-bold font-mono">{debugResult.diagnosis}</p>
                {debugResult.diagnosis === 'ip_not_whitelisted' && (
                  <p className="text-xs mt-2">
                    Tripjack rejected the request at the gateway level. Your server's outbound IP is not on Tripjack's whitelist for this account/env. Contact your Tripjack account manager and share the exact production egress IP.
                  </p>
                )}
                {debugResult.diagnosis === 'wallet_balance_zero' && (
                  <p className="text-xs mt-2">Your Tripjack wallet has zero balance. Top up via the Tripjack agent portal before searches will return live flights.</p>
                )}
                {debugResult.diagnosis === 'api_key_invalid' && (
                  <p className="text-xs mt-2">Tripjack rejected the API key. Re-check the value pasted into Supplier Configs.</p>
                )}
                {debugResult.diagnosis === 'no_carriers_whitelisted' && (
                  <p className="text-xs mt-2">No airlines are whitelisted on this account. Contact Tripjack to enable carriers.</p>
                )}
              </div>
              {debugResult.supplier_response?.errors?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-1">Supplier errors</p>
                  <ul className="space-y-1">
                    {debugResult.supplier_response.errors.map((e, i) => (
                      <li key={i} className="text-xs font-mono bg-gray-50 border border-gray-200 rounded p-2">
                        <span className="text-rose-700 font-bold">{e.errCode || e.code || '?'}</span> — {e.message || e.desc || JSON.stringify(e)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-gray-700">Full raw response (for support tickets)</summary>
                <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-[10px] leading-tight">
{JSON.stringify(debugResult.supplier_response, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
